import { Component, type ErrorInfo, type ReactNode } from "react";
import GlobalErrorDialog from "@/components/errors/GlobalErrorDialog";
import { formatError } from "@/lib/formatError";

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  private handleDismiss = () => {
    this.setState({ error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      const { details } = formatError(this.state.error);

      return (
        <div className="min-h-screen bg-background">
          <GlobalErrorDialog
            open
            details={details}
            onOpenChange={(open) => {
              if (!open) this.handleDismiss();
            }}
            onReload={this.handleReload}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
